/**
 * Express checkout START analytics for the cart page and the slide-out cart.
 *
 * Emits the OnlineService beacon event `express_checkout_start` (analytics_route endpoint)
 * when a shopper starts checkout via a wallet button rendered from
 * cart.additional_checkout_buttons. Scope: .cart-additionalCheckoutButtons (cart page) and
 * .side-cart-express (drawer) ONLY — the add-to-cart popup modal also renders these buttons
 * but is intentionally untracked.
 *
 * The VISUAL express-checkout concern (hiding the empty Apple Pay cell off-Apple) lives in
 * f/express-checkout.js, init'd from global/cart-preview.js. This module is analytics only.
 *
 * Known accepted measurement limits (spec 2026-06-11-express-checkout-start-tracking-design.md):
 * - PayPal renders in a cross-origin zoid iframe, so its starts are detected by a window-blur
 *   heuristic (detection: 'blur'), which UNDERCOUNTS on iOS/mobile Safari (iframe taps don't
 *   reliably blur the window; popup-blocked flows full-page redirect). Segment PayPal rows by
 *   device class before cross-method comparison.
 * - Keyboard-Tab into the PayPal iframe registers as a start (rare, one-directional, and
 *   discountable via the detection field).
 *
 * Module top level must stay free of document/window access, and @bigcommerce/stencil-utils
 * must only ever be imported dynamically inside handlers (its module scope throws under
 * Vitest's node environment) — this keeps the pure helpers importable by the unit tests.
 */
import { sendInteractionEvent } from '../custom/event/analytics-sender';
import { bus, Events } from '../common/event-bus';

const TRACKED_CONTAINERS = ['.cart-additionalCheckoutButtons', '.side-cart-express'];

/**
 * Classify a wallet button from any element inside its .CheckoutButton wrapper.
 * Fail-open: an unrecognized wrapper returns 'unknown' (the event still fires) so a
 * provider/markup drift degrades buttonType, never the count. Only .ApplePayCheckout and
 * .paypal-buttons-container are corroborated by tracked files in this repo; the Braintree /
 * GooglePay names (incl. BraintreePaypalCreditCheckout = PayPal Credit / Pay Later) were
 * observed live on the SSW sandbox (Braintree provider) and may differ per store. Uses only
 * closest() + classList.contains() so node-env tests can pass stubs.
 *
 * @param {Element|null} el
 * @returns {'paypal'|'paypal_credit'|'google_pay'|'apple_pay'|'venmo'|'unknown'|null} null = not a wallet button.
 */
export function identifyExpressMethod(el) {
    const wrapper = el?.closest?.('.CheckoutButton');
    if (!wrapper) {
        return null;
    }
    const has = (cls) => wrapper.classList.contains(cls);
    // PayPal Credit / Pay Later is a distinct wrapper that does NOT carry the
    // BraintreePaypalCheckout token — check it before the plain PayPal branch.
    if (has('BraintreePaypalCreditCheckout')) {
        return 'paypal_credit';
    }
    if (has('BraintreePaypalCheckout') || has('paypal-buttons-container')) {
        return 'paypal';
    }
    if (has('GooglePayCheckout')) {
        return 'google_pay';
    }
    if (has('ApplePayCheckout')) {
        return 'apple_pay';
    }
    if (has('BraintreeVenmoCheckout')) {
        return 'venmo';
    }
    return 'unknown';
}

/**
 * Which tracked surface the wallet button belongs to.
 *
 * @param {Element|null} el
 * @returns {'cart_page'|'side_cart'|null} null = untracked container (e.g. popup modal) or
 *   not a wallet button at all — callers must emit nothing.
 */
export function getExpressSource(el) {
    const wrapper = el?.closest?.('.CheckoutButton');
    if (!wrapper) {
        return null;
    }
    if (wrapper.closest('.cart-additionalCheckoutButtons')) {
        return 'cart_page';
    }
    if (wrapper.closest('.side-cart-express')) {
        return 'side_cart';
    }
    return null;
}

/**
 * The express_checkout_start metaData payload. Identity (DeviceId/VisitorId/SessionId) is
 * merged by sendInteractionEvent — this is only the event-specific part. Shape mirrors
 * remove_from_cart_start (side-cart.js): flat keys + ISO timestamp.
 *
 * @param {string} buttonType
 * @param {string} source
 * @param {string|null|undefined} cartId - empty/undefined normalized to null; never blocks.
 * @param {'click'|'blur'} detection - lets analysts segment the blur-derived PayPal rows.
 * @param {Date} [now] - injectable for tests.
 */
export function buildExpressCheckoutStartPayload(buttonType, source, cartId, detection, now = new Date()) {
    return {
        buttonType,
        source,
        cartId: cartId || null,
        detection,
        timestamp: now.toISOString(),
    };
}

// ─── Runtime state ───────────────────────────────────────────────────────────
// cartId: window.getCartId() is the free render-time source ('{{cart_id}}' from
// init-globals.html), empty when the cart was created via AJAX during this page's
// lifetime. cachedCartId fills that gap via the triple-guarded lazy refresh below.
let cachedCartId = null;
let cartIdFetchInFlight = false;
// PayPal blur latch: fire only on the armed→fired transition (one event per arm).
let blurArmed = true;

function resolveCartId() {
    const renderTimeId = typeof window.getCartId === 'function' ? window.getCartId() : '';
    return renderTimeId || cachedCartId || null;
}

function emitExpressCheckoutStart(buttonType, source, detection) {
    // Fire-and-forget: the fetch fallback in the sender THROWS on network/HTTP failure,
    // and nothing here may delay or break the wallet SDK's own click handling.
    sendInteractionEvent(
        'express_checkout_start',
        buildExpressCheckoutStartPayload(buttonType, source, resolveCartId(), detection),
    ).catch(() => {});
}

/**
 * Lazy cartId refresh for the AJAX-created-cart gap (PDP add-to-cart → drawer → express
 * click). CART_UPDATED fires 1-2x on every ordinary page load and carries only { quantity },
 * so the triple guard below is what keeps this from becoming a per-pageview API call:
 * only when no id is known yet, a tracked express button actually exists, and no fetch is
 * already in flight — at most one getCart() per page lifetime.
 */
function maybeRefreshCartId() {
    if (resolveCartId() || cartIdFetchInFlight) {
        return;
    }
    const hasExpressButtons = document.querySelector(
        TRACKED_CONTAINERS.map((c) => `${c} .CheckoutButton`).join(', '),
    );
    if (!hasExpressButtons) {
        return;
    }
    cartIdFetchInFlight = true;
    // Deferred import: stencil-utils' module scope is not node-safe (see header docblock).
    import('@bigcommerce/stencil-utils')
        .then(({ default: utils }) => {
            utils.api.cart.getCart({}, (err, response) => {
                cartIdFetchInFlight = false;
                if (!err && response && response.id) {
                    cachedCartId = response.id;
                }
            });
        })
        .catch(() => {
            cartIdFetchInFlight = false;
        });
}

/**
 * Click path: Google Pay / Apple Pay / Venmo (real light-DOM elements).
 * Registered CAPTURE-PHASE so a wallet SDK's stopPropagation() can never silently zero a
 * method's count, and read-only (no preventDefault/stopPropagation) so Apple Pay's
 * user-gesture requirement and the SDKs' own handlers are untouched.
 * PayPal wrappers are EXCLUDED here: real PayPal presses happen inside the cross-origin
 * iframe and never reach this document — a parent-document click on the PayPal wrapper is a
 * dead-zone click (off the iframe), not a checkout start.
 */
function onDocumentClickCapture(event) {
    try {
        const method = identifyExpressMethod(event.target);
        if (!method || method === 'paypal') {
            return;
        }
        const source = getExpressSource(event.target);
        if (!source) {
            return;
        }
        emitExpressCheckoutStart(method, source, 'click');
    } catch (err) {
        // Analytics must never interfere with checkout.
    }
}

/**
 * Blur path: PayPal (cross-origin iframe). When the window blurs because focus moved INTO
 * the wallet iframe, document.activeElement is that iframe element (which lives in the
 * parent DOM). Armed-latch + post-fire focus-clearing: without pulling focus back, the
 * parent window never re-blurs after the first click, so a cancel→retry PayPal click would
 * never be counted (the naive heuristic's primary defect is UNDERCOUNTING, not duplicates).
 */
function onWindowBlur() {
    try {
        const el = document.activeElement;
        if (!el || el.tagName !== 'IFRAME') {
            return; // cheap first-line bail — the every-page cost is one comparison
        }
        const method = identifyExpressMethod(el);
        if (!method) {
            return; // iframe not inside a .CheckoutButton wrapper
        }
        const source = getExpressSource(el);
        if (!source) {
            return; // untracked container (e.g. popup modal)
        }
        if (!blurArmed) {
            // Sub-500ms re-blur is debounced away (intended — the focus-clear below
            // would otherwise re-trigger this handler and double-fire).
            return;
        }
        blurArmed = false;
        emitExpressCheckoutStart(method, source, 'blur');
        setTimeout(() => {
            try {
                el.blur();
                window.focus();
            } catch (focusErr) {
                // ignore — re-arm regardless
            }
            blurArmed = true;
        }, 500);
    } catch (err) {
        // Analytics must never interfere with checkout.
    }
}

/**
 * Init once from global.js onReady. Internally guarded: a failure here must never break
 * the rest of onReady or the wallet buttons themselves.
 */
export default function initExpressCheckoutTracking() {
    try {
        document.addEventListener('click', onDocumentClickCapture, true);
        window.addEventListener('blur', onWindowBlur);
        bus.on(Events.CART_UPDATED, maybeRefreshCartId);
    } catch (err) {
        // Analytics must never break the page.
    }
}
